// ── v2 Softening materials system ─────────────────────────────────────────
//
// Canonical material registry for the Softening-era loot model. Replaces the
// v1 rarity-tier equipment ladder at the *design* level while coexisting in
// code with the legacy `ItemType` enum in `../types/loot.ts` until a later
// migration phase sweeps v1 references.
//
// Source of truth: `docs/design/loot-and-materials.md` §2.
//
// Pure module: no runtime dependencies beyond TypeScript. No I/O, no Colyseus,
// no network imports. Safe to consume from either package (client/server).
/**
 * Every v2 material in the closed palette defined by
 * `docs/design/loot-and-materials.md` §2.
 *
 * String enum; values are lowercase kebab-case so they round-trip cleanly
 * through JSON, URL params, and Colyseus schema strings without escaping.
 */
export var MaterialKey;
(function (MaterialKey) {
    /** Most common. The heartbeat resource — homes, hearths, lanterns, furniture, rituals. */
    MaterialKey["Wood"] = "wood";
    /** Second-most common. Shrines, foundations, pillars, wells. Heavier per-gesture cost. */
    MaterialKey["Stone"] = "stone";
    /** Roofs, beds, small ritual bindings. */
    MaterialKey["Thatch"] = "thatch";
    /** Paper cranes, journal bindings, ritual inscriptions. Ground from specific seeds + water. */
    MaterialKey["Ink"] = "ink";
    /** Candles, lanterns, ritual seals. From beehives and seasonal flowers. */
    MaterialKey["Wax"] = "wax";
    /** Bedding, cranes (folded from cloth for special deliveries), clothing. */
    MaterialKey["Cloth"] = "cloth";
    /** Frostmere rituals (warming), cooking flavor, ink-making. Byproduct of unburdening. */
    MaterialKey["Ash"] = "ash";
    /** Frostmere-specific; feeds regrowth. Only from the Frostmere thaw line. */
    MaterialKey["ThawedWater"] = "thawed-water";
    /** Ember Mire–specific; kindles fires, feeds forges, rituals. Strongly regional. */
    MaterialKey["Ember"] = "ember";
    /** Rare; a hummed note captured in a small wooden jar. Shrine-adjacent events. */
    MaterialKey["Spiritsong"] = "spiritsong";
})(MaterialKey || (MaterialKey = {}));
/**
 * Canonical metadata registry for every v2 material. Keyed by `MaterialKey`
 * so it is exhaustive-by-construction at the type level.
 *
 * Population follows `docs/design/loot-and-materials.md` §2 verbatim. Realm
 * hints follow the "regional material" notes: thawed-water → frostmere,
 * ember → embermire, spiritsong → any (rare shrine-adjacent drops), all
 * others → any.
 */
export const MATERIALS = {
    [MaterialKey.Wood]: {
        key: MaterialKey.Wood,
        displayName: 'Wood',
        description: 'Most common. The heartbeat resource — homes, hearths, lanterns, furniture, some rituals.',
        realmHint: 'any',
        abundant: true,
    },
    [MaterialKey.Stone]: {
        key: MaterialKey.Stone,
        displayName: 'Stone',
        description: 'Second-most common. Shrines, foundations, pillars, wells. Heavier per-gesture cost.',
        realmHint: 'any',
        abundant: true,
    },
    [MaterialKey.Thatch]: {
        key: MaterialKey.Thatch,
        displayName: 'Thatch',
        description: 'Roofs, beds, small ritual bindings.',
        realmHint: 'any',
        abundant: true,
    },
    [MaterialKey.Ink]: {
        key: MaterialKey.Ink,
        displayName: 'Ink',
        description: 'Paper cranes, journal bindings, ritual inscriptions. Ground from specific seeds and water.',
        realmHint: 'any',
        abundant: false,
    },
    [MaterialKey.Wax]: {
        key: MaterialKey.Wax,
        displayName: 'Wax',
        description: 'Candles, lanterns, ritual seals. Gathered from beehives and seasonal flowers.',
        realmHint: 'any',
        abundant: false,
    },
    [MaterialKey.Cloth]: {
        key: MaterialKey.Cloth,
        displayName: 'Cloth',
        description: 'Bedding, cranes folded for special deliveries, clothing. Woven from plants or scraped from Softened creatures.',
        realmHint: 'any',
        abundant: false,
    },
    [MaterialKey.Ash]: {
        key: MaterialKey.Ash,
        displayName: 'Ash',
        description: 'Frostmere rituals (warming), cooking flavor, ink-making. Byproduct of unburdening.',
        realmHint: 'any',
        abundant: false,
    },
    [MaterialKey.ThawedWater]: {
        key: MaterialKey.ThawedWater,
        displayName: 'Thawed Water',
        description: 'Frostmere-specific; feeds regrowth. Only from the Frostmere thaw line; strongly regional.',
        realmHint: 'frostmere',
        abundant: false,
    },
    [MaterialKey.Ember]: {
        key: MaterialKey.Ember,
        displayName: 'Ember',
        description: 'Ember Mire–specific; kindles fires, feeds forges, rituals. Strongly regional.',
        realmHint: 'embermire',
        abundant: false,
    },
    [MaterialKey.Spiritsong]: {
        key: MaterialKey.Spiritsong,
        displayName: 'Spiritsong',
        description: 'Rare; a hummed note captured in a small wooden jar. Drops from rare shrine-adjacent events; used in Expedition opening rituals.',
        realmHint: 'any',
        abundant: false,
    },
};
/**
 * Static display order for ledgers, inventory UI, and drop-table listings.
 *
 * Chosen order:
 *   1. Abundant baselines (Wood, Stone, Thatch) — most-touched first, in
 *      per-gesture cost order so the heartbeat resource is always on top.
 *   2. Crafted / gathered non-regionals (Ink, Wax, Cloth, Ash) — the
 *      "workshop" cluster; grouped together for recipe legibility.
 *   3. Strongly regional materials (Thawed Water, Ember) — surfaced after
 *      the universal palette so players read their kit from common → rare.
 *   4. Rare shrine drop (Spiritsong) — last because it's the ritual capstone.
 */
export const MATERIAL_DISPLAY_ORDER = [
    // Abundant baselines
    MaterialKey.Wood,
    MaterialKey.Stone,
    MaterialKey.Thatch,
    // Workshop cluster
    MaterialKey.Ink,
    MaterialKey.Wax,
    MaterialKey.Cloth,
    MaterialKey.Ash,
    // Strongly regional
    MaterialKey.ThawedWater,
    MaterialKey.Ember,
    // Rare capstone
    MaterialKey.Spiritsong,
];
/**
 * Retrieve the canonical metadata record for a material.
 *
 * Since `MATERIALS` is keyed by `MaterialKey` (an exhaustive enum), the
 * lookup is total — any valid `MaterialKey` returns a defined record.
 *
 * @param key - Material to look up.
 * @returns The `MaterialMetadata` record for `key`.
 */
export function getMaterial(key) {
    return MATERIALS[key];
}
/**
 * Purely-functional addition of two material ledgers.
 *
 * Neither input is mutated. Entries with a resulting count of zero are
 * *omitted* from the output (omit-zero behavior) so downstream serialization
 * stays compact and set-equality checks work naturally.
 *
 * Commutative: `addMaterials(a, b)` deep-equals `addMaterials(b, a)`.
 * Associative: `addMaterials(addMaterials(a, b), c)` deep-equals
 * `addMaterials(a, addMaterials(b, c))`.
 *
 * @param a - Left operand ledger.
 * @param b - Right operand ledger.
 * @returns A fresh `MaterialCount` with summed counts; zero entries omitted.
 */
export function addMaterials(a, b) {
    const out = {};
    const keys = new Set([
        ...Object.keys(a),
        ...Object.keys(b),
    ]);
    for (const k of keys) {
        const sum = (a[k] ?? 0) + (b[k] ?? 0);
        if (sum !== 0) {
            out[k] = sum;
        }
    }
    return out;
}
/**
 * Purely-functional subtraction with atomic-transaction semantics.
 *
 * Returns a fresh ledger representing `a - b`. If *any* resulting count
 * would be negative, returns `null` and performs no subtraction — this is
 * the contract for ritual costs: either the full cost is paid or nothing
 * changes. Never returns a ledger with a negative entry.
 *
 * Entries with a resulting count of zero are omitted from the output.
 *
 * @param a - Ledger to subtract from (the "have" side).
 * @param b - Ledger to subtract (the "cost" side).
 * @returns A fresh `MaterialCount` with the differences, or `null` if `b`
 *          exceeds `a` for any material.
 */
export function subMaterials(a, b) {
    // First pass — validate sufficiency. Atomic: we don't mutate anything
    // until we know the full transaction can complete.
    for (const k of Object.keys(b)) {
        const cost = b[k] ?? 0;
        const have = a[k] ?? 0;
        if (cost > have)
            return null;
    }
    // Second pass — compute result. Zero entries are omitted.
    const out = {};
    const keys = new Set([
        ...Object.keys(a),
        ...Object.keys(b),
    ]);
    for (const k of keys) {
        const diff = (a[k] ?? 0) - (b[k] ?? 0);
        if (diff !== 0) {
            out[k] = diff;
        }
    }
    return out;
}
/**
 * Non-destructive sufficiency check. True iff `have` contains at least
 * `need[k]` of every material `k` listed in `need`.
 *
 * Equivalent to `subMaterials(have, need) !== null` but avoids allocating
 * a result ledger when the caller only needs the boolean.
 *
 * @param have - Ledger representing what the player currently owns.
 * @param need - Ledger representing the cost to check.
 * @returns True if the transaction would succeed.
 */
export function hasMaterials(have, need) {
    for (const k of Object.keys(need)) {
        const cost = need[k] ?? 0;
        const owned = have[k] ?? 0;
        if (cost > owned)
            return false;
    }
    return true;
}
//# sourceMappingURL=index.js.map